import React, { useEffect, useState } from "react";
import { Alert, Button, DatePicker, Form, Input, Spin, message } from "antd";
import dayjs from "dayjs";
import {
  createSprNumberPrice,
  fetchSprNumberPrice,
  saveSprNumberPrice
} from "./spr-number-price-api";
import { normalizeZag } from "./spr-number-price-utils";

const LABEL_WIDTH = 180;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const toDay = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

/**
 * Форма шапки прайс-листа. Delphi: UESprNumberPriceZag.
 */
const SprNumberPriceCard = ({ idNumberPrice, isNew = false, open, onSaved, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    if (isNew) {
      setLoading(false);
      setError("");
      const today = dayjs().startOf("day");
      form.setFieldsValue({
        name: "",
        dateBeg: today,
        dateEnd: today
      });
      return () => {
        cancelled = true;
      };
    }

    if (idNumberPrice == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprNumberPrice(idNumberPrice)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeZag(row);
        form.setFieldsValue({
          idNumberPrice: card.idNumberPrice,
          name: card.name,
          dateBeg: toDay(card.dateBeg),
          dateEnd: toDay(card.dateEnd)
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить прайс");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idNumberPrice, form]);

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = {
      name: (values.name || "").trim(),
      dateBeg: values.dateBeg ? values.dateBeg.format("YYYY-MM-DD") : null,
      dateEnd: values.dateEnd ? values.dateEnd.format("YYYY-MM-DD") : null
    };

    try {
      if (isNew) {
        const created = await createSprNumberPrice(body);
        message.success("Сохранено");
        onSaved?.(created);
      } else {
        const saved = await saveSprNumberPrice({ ...body, idNumberPrice });
        message.success("Сохранено");
        onSaved?.(saved);
      }
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="spr-number-price-card__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  return (
    <div className="spr-number-price-card">
      <div className="spr-number-price-card__body">
        {error ? (
          <Alert className="spr-number-price-card__error" type="error" showIcon message={error} />
        ) : null}
        <Form
          form={form}
          className="spr-number-price-card__form"
          {...formItemLayout}
          colon={false}
          onFinish={onFinish}
          disabled={saving}
        >
          {!isNew ? (
            <Form.Item label="#" name="idNumberPrice">
              <Input disabled />
            </Form.Item>
          ) : null}
          <Form.Item
            label="Начало действия"
            name="dateBeg"
            rules={[{ required: true, message: "Укажите дату начала" }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Окончание действия"
            name="dateEnd"
            rules={[{ required: true, message: "Укажите дату окончания" }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Наименование"
            name="name"
            rules={[{ required: true, message: "Укажите наименование" }]}
          >
            <Input maxLength={80} />
          </Form.Item>
        </Form>
      </div>
      <div className="spr-number-price-card__footer">
        <Button type="primary" loading={saving} onClick={() => form.submit()}>
          Сохранить
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprNumberPriceCard;
